import _ from "lodash";
import { Stats } from "../common";

interface DimensionDefinition {
  displayLabel: string;
  units: string;
  labelValue(value: number): string;
  divisions: number[];
  stats?: Partial<Stats>;
}

function defaultDimension(dimensionName: string): DimensionDefinition {
  return {
    displayLabel: (dimensionName || "")
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .toLowerCase(),
    units: "",
    labelValue(value: number) {
      return value.toFixed(1).toString();
    },
    divisions: [0.5, 1, 2, 5, 10, 25, 50, 100, 250, 500, 1000, 5000],
  };
}

const allDimensions: { [index: string]: Partial<DimensionDefinition> } = {
  verticalSpeed: {
    labelValue(feetPerHour) {
      return `${Math.round(feetPerHour)}'/h`;
    },
    units: "feet/hour",
  },

  days: {
    labelValue(days) {
      return `${days} days`;
    },
  },

  distancePerWeek: {
    labelValue(n) {
      return n % 1 === 0 ? n.toString() : n.toFixed(1);
    },

    divisions: [0.1, 0.25, 0.5, 1],
  },

  speedMph: {
    labelValue(value) {
      const pace = 60 / value;
      const minutes = Math.floor(pace);
      const seconds = Math.floor((pace - minutes) * 60);
      return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}/mi`;
    },
    displayLabel: "pace",
    units: "min/mi",
    stats: { min: 4, max: 10, range: 6 },
  },

  smoothedVelocityMph: {
    labelValue(value) {
      const pace = 60 / value;
      const minutes = Math.floor(pace);
      const seconds = Math.floor((pace - minutes) * 60);
      return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}/mi`;
    },
    displayLabel: "pace",
    units: "min/mi",
    stats: { min: 4, max: 10, range: 6 },
  },

  gradeSpeedEquivalentMph: {
    displayLabel: "grade equivalent pace",
    units: "min/mi",
    labelValue(gse) {
      const pace = 60 / gse;
      const minutes = Math.floor(pace);
      const seconds = Math.floor((pace - minutes) * 60);
      return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}/mi`;
    },
    stats: { min: 4, max: 10, range: 6 },
  },

  gradePerMi: {
    labelValue(value) {
      return `${Math.round(value)}'/mi`;
    },
    displayLabel: "grade",
    units: "feet per mile",
  },

  smoothedGrade: {
    labelValue(value) {
      return `${Math.round(value)}'/mi`;
    },
    displayLabel: "grade",
    units: "feet per mile",
  },

  meanHeartrate: {
    labelValue(hr) {
      return `${Math.round(hr)}bpm`;
    },
    displayLabel: "heart rate",
    units: "bpm",
  },

  smoothedHeartrate: {
    labelValue(hr) {
      return `${Math.round(hr)}bpm`;
    },
    displayLabel: "heart rate",
    units: "bpm",
  },

  grade: {
    labelValue(value) {
      return `${Math.round(value * 100)}%`;
    },
    displayLabel: "gradient",
    units: "percent rise/run",
  },
  angle: {
    labelValue(angle) {
      return `${Math.round(angle)}º`;
    },
    units: "degrees",
  },

  paceMinMi: {
    labelValue(pace) {
      const minutes = Math.floor(pace);
      const seconds = Math.floor((pace - minutes) * 60);
      return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}/mi`;
    },
    displayLabel: "pace",
    units: "min/mi",
  },

  smoothedPaceMinMi: {
    labelValue(pace) {
      const minutes = Math.floor(pace);
      const seconds = Math.floor((pace - minutes) * 60);
      return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}/mi`;
    },
    displayLabel: "pace",
    units: "min/mi",
  },

  meanCumulativeDistanceMi: {
    labelValue(distance) {
      return `${distance.toFixed(1)}mi`;
    },
    displayLabel: "distance",
    units: "miles",
  },

  distanceMi: {
    labelValue(distance) {
      return `${distance.toFixed(1)}mi`;
    },
    displayLabel: "distance",
    units: "miles",
  },

  timeMillis: {
    displayLabel: "time",
    units: "h:mm",
    labelValue(value) {
      const hours = Math.floor(value / (60 * 60 * 1000));
      const minutes = Math.floor(value / (60 * 1000) - hours * 60);
      return `${hours}:${minutes < 10 ? `0${minutes}` : minutes}`;
    },
    divisions: [
      1000, //1s
      1000 * 60, //1min
      1000 * 60 * 5, //5min
      1000 * 60 * 15, //15min
      1000 * 60 * 30, //30min
      1000 * 60 * 60, //1h
      1000 * 60 * 60 * 2, //2h,
      1000 * 60 * 60 * 3, //3h,
      1000 * 60 * 60 * 4, //4h,
      1000 * 60 * 60 * 5, //5h,
    ],
  },

  elapsedTimeMillis: {
    displayLabel: "time",
    units: "h:mm",
    labelValue(value) {
      const hours = Math.floor(value / (60 * 60 * 1000));
      const minutes = Math.floor(value / (60 * 1000) - hours * 60);
      return `${hours}:${minutes < 10 ? `0${minutes}` : minutes}`;
    },
    divisions: [
      1000, //1s
      1000 * 60, //1min
      1000 * 60 * 5, //5min
      1000 * 60 * 15, //15min
      1000 * 60 * 30, //30min
      1000 * 60 * 60, //1h
      1000 * 60 * 60 * 2, //2h,
      1000 * 60 * 60 * 3, //3h,
      1000 * 60 * 60 * 4, //4h,
      1000 * 60 * 60 * 5, //5h,
    ],
  },

  timeSeconds: {
    displayLabel: "time",
    units: "h:mm",
    labelValue(seconds) {
      const hours = Math.floor(seconds / (60 * 60));
      const minutes = Math.floor(seconds / 60 - hours * 60);
      return `${hours}:${minutes < 10 ? `0${minutes}` : minutes}`;
    },
    divisions: [1, 60, 60 * 15, 60 * 30, 60 * 60, 60 * 5],
  },

  meanElevation: {
    displayLabel: "elevation",
    units: "feet",
    labelValue(elevation) {
      const numberFormat = new Intl.NumberFormat();
      return `${numberFormat.format(Math.floor(elevation))}'`;
    },
  },

  elevation: {
    displayLabel: "elevation",
    units: "feet",
    labelValue(elevation) {
      const numberFormat = new Intl.NumberFormat();
      return `${numberFormat.format(Math.floor(elevation))}'`;
    },
  },

  strideLengthFt: {
    displayLabel: "stride length",
    units: "feet",
    labelValue(strideLength) {
      return `${strideLength.toFixed(2)}'`;
    },
  },

  cadence: {
    units: "spm",
    labelValue(cadence) {
      return `${Math.floor(cadence * 2)}/min`;
    },
  },

  smoothedCadence: {
    units: "spm",
    displayLabel: "cadence",
    labelValue(cadence) {
      return `${Math.floor(cadence * 2)}/min`;
    },
  },

  smoothGradeAdjustedVelocity: {
    displayLabel: "relative speed",
    units: "mph",
    labelValue(sgav) {
      return sgav.toFixed(1);
    },
    divisions: [0.1, 0.25, 0.5, 1, 2, 5],
  },

  gradeSpeedZScore: {
    displayLabel: "personal z",
    units: "z",
    labelValue(p) {
      return `${p.toFixed(2)}z`;
    },

    stats: { min: -10, max: 10, range: 20 },
  },

  smoothZScore: {
    displayLabel: "smooth z",
    units: "z",
    labelValue(p) {
      return `${p.toFixed(2)}z`;
    },

    stats: { min: -10, max: 10, range: 20 },
  },

  durationEffortPercentile: {
    displayLabel: "duration-adjusted personal effort",
    units: "percent",
    labelValue(p) {
      return `${p.toFixed(0)}%`;
    },
    stats: { min: 0, max: 100, range: 100 },
  },

  durationEffortDominantDuration: {
    displayLabel: "dominant duration",
    labelValue(seconds) {
      if (seconds < 60) {
        return `${seconds.toFixed(1)}s`;
      } else if (seconds < 3600) {
        const minutes = seconds / 60;
        return minutes % 1 === 0 ? `${minutes}min` : `${minutes.toFixed(1)}min`;
      } else {
        const hours = seconds / 3600;
        return hours % 1 === 0 ? `${hours}hr` : `${hours.toFixed(1)}hr`;
      }
    },
  },

  dominantDurationRatio: {
    displayLabel: "duration ratio",
    labelValue(p) {
      return `${(p * 100).toFixed(0)}%`;
    },
    stats: { min: 0, max: 1, range: 1 },
  },

  watts: {
    displayLabel: "power",
    units: "watts",
    labelValue(w) {
      return `${w.toFixed(0)}w`;
    },
  },

  smoothedWatts: {
    displayLabel: "power",
    units: "watts",
    labelValue(w) {
      return `${w.toFixed(0)}w`;
    },
  },

};

export function getDimension<T>(
  dimensionName: Dimension<T> | Function,
): DimensionDefinition {
  if (typeof dimensionName === "string" && dimensionName in allDimensions) {
    return {
      ...defaultDimension(dimensionName as string),
      ...allDimensions[dimensionName as string],
    };
  } else {
    const name =
      typeof dimensionName === "function"
        ? dimensionName.name
        : (dimensionName as string);
    return defaultDimension(name);
  }
}

export const dimensionsWithLabels = (...arr: string[]) =>
  _.flatten(arr).reduce(
    (o, k) => ({ ...o, [k]: getDimension(k).displayLabel }),
    {},
  );
const dimensions = [
  "meanCumulativeDistanceMi",
  "index",
  "points",
  "cadence",
  "distanceMi",
  "netElevationChange",
  "grossElevationChange",
  "elapsedTimeMillis",
  "timeMillis",
  "meanHeartrate",
  "meanElevation",
  "grade",
  "gradePerMi",
  "angle",
  "speedMph",
  "smoothedVelocityMph",
  "paceMinMi",
  "verticalSpeed",
  "gain",
  "loss",
  "gainPerMi",
  "lossPerMi",
  "smoothedHeartrate",
  "smoothedCadence",
  "gradeSpeedZScore",
  "smoothZScore",
  "gradeSpeedEquivalentMph",
  "watts",
  "smoothedWatts",
  "durationEffortPercentile",
  "durationEffortDominantDuration",
] as const;

export const dimensionList = dimensionsWithLabels(...dimensions);

export type Dimension<T> = keyof T;

export const SUMMARY_STATS = [
  "mean",
  "min",
  "max",
  "variance",
  "stdev",
  "range",
  "median",
  "count",
  "sum",
] as const;

export type SummaryStat = (typeof SUMMARY_STATS)[number];

export const X_DIMENSIONS = dimensionsWithLabels(
  "meanCumulativeDistanceMi",
  "timeMillis",
);
